import { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import MDButton from "components/MDButton";

function SuratMasukDokumen() {
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [levelJabatan, setLevelJabatan] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [suratList, setSuratList] = useState([]);
  const [filteredSuratList, setFilteredSuratList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());
  const [selectedBulan, setSelectedBulan] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limitPerPage, setLimitPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("terbaru"); // terbaru, terlama
  const [viewMode, setViewMode] = useState("grid"); // grid, list

  // Dialog PDF state
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Generate tahun list (current year down to 2025)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push({ label: i.toString(), value: i });
  }

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserId(user.uid);
      setLevelJabatan(user.level_jabatan);
      setUserStatus(user.status);
    } else {
      logoutUser(navigate);
    }
  }, [navigate]);

  const fetchSuratMasuk = async () => {
    setLoading(true);
    try {
      // Tentukan endpoint berdasarkan level_jabatan dan status
      const isAdminOrLevel12 =
        userStatus === "admin" ||
        userStatus === "super admin" ||
        levelJabatan === 1 ||
        levelJabatan === 2;

      let url;
      if (isAdminOrLevel12) {
        // User dengan level_jabatan 1 atau 2 atau status admin: gunakan endpoint dinas
        url = `${API_ESA_OFFICE}surat-masuk/dinas/${dinasId}`;
      } else {
        // User biasa: gunakan endpoint user (hanya surat yang terkait)
        url = `${API_ESA_OFFICE}surat-masuk/user/${userId}`;
      }

      const params = [];
      if (selectedTahun) {
        params.push(`tahun=${selectedTahun}`);
      }
      if (selectedBulan) {
        params.push(`bulan=${selectedBulan}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await axios.get(url);
      let data = response.data || [];

      // Sorting berdasarkan sortBy
      if (sortBy === "terlama") {
        data = [...data].sort(
          (a, b) => new Date(a.tanggal_surat) - new Date(b.tanggal_surat)
        );
      }
      // terbaru adalah default (sudah diurutkan dari backend)

      // Filter berdasarkan search term
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        data = data.filter((surat) => {
          const nomorSurat = (surat.nomor_surat || "").toLowerCase();
          const pengirim = (surat.pengirim || "").toLowerCase();
          const perihal = (surat.perihal || "").toLowerCase();
          return (
            nomorSurat.includes(searchLower) ||
            pengirim.includes(searchLower) ||
            perihal.includes(searchLower)
          );
        });
      }

      // Pagination
      const totalData = data.length;
      const totalPagesData = Math.ceil(totalData / limitPerPage);
      const startIndex = (currentPage - 1) * limitPerPage;
      const endIndex = startIndex + limitPerPage;
      const paginatedData = data.slice(startIndex, endIndex);

      setSuratList(data);
      setFilteredSuratList(paginatedData);
      setTotal(totalData);
      setTotalPages(totalPagesData);
    } catch (error) {
      console.error("Error fetching surat masuk:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId && userId && levelJabatan !== null && userStatus !== null) {
      setCurrentPage(1); // Reset ke halaman 1 saat filter berubah
    }
  }, [
    selectedTahun,
    selectedBulan,
    searchTerm,
    dinasId,
    userId,
    levelJabatan,
    userStatus,
    sortBy,
  ]);

  useEffect(() => {
    if (dinasId && userId && levelJabatan !== null && userStatus !== null) {
      fetchSuratMasuk();
    }
  }, [
    dinasId,
    userId,
    levelJabatan,
    userStatus,
    selectedTahun,
    selectedBulan,
    searchTerm,
    limitPerPage,
    sortBy,
    currentPage,
  ]);

  // Handler untuk perubahan halaman
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format waktu relatif
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "Baru saja";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} menit yang lalu`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} jam yang lalu`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} hari yang lalu`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} bulan yang lalu`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} tahun yang lalu`;
  };

  // Handler untuk membuka file
  const handleFileClick = (surat) => {
    if (!surat.file_surat) return;

    const fileUrl = `${API_ESA_OFFICE_UPLOAD}${surat.file_surat}`;
    const fileExtension =
      surat.file_surat.split(".").pop()?.toLowerCase() || "";
    const isPdf = fileExtension === "pdf";

    if (isPdf) {
      // PDF: buka di dialog
      setSelectedFileUrl(fileUrl);
      setOpenFileDialog(true);
    } else {
      // Non-PDF: buka di tab baru
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Handler untuk menutup dialog PDF
  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFileUrl(null);
  };

  const getFileIcon = (surat) => {
    if (!surat.file_surat)
      return <Icon fontSize="small">insert_drive_file</Icon>;

    const fileExtension =
      surat.file_surat.split(".").pop()?.toLowerCase() || "";
    if (fileExtension === "pdf") {
      return <Icon fontSize="small">picture_as_pdf</Icon>;
    }
    return <Icon fontSize="small">description</Icon>;
  };

  const getFileButtonLabel = (surat) => {
    if (!surat.file_surat) return "Tidak ada file";

    const fileExtension =
      surat.file_surat.split(".").pop()?.toLowerCase() || "";
    if (fileExtension === "pdf") {
      return "Buka PDF";
    }
    return "Download";
  };

  // Handler untuk navigasi ke halaman Disposisi Baru
  const handleDisposisi = (surat) => {
    navigate("/task-management/disposisi/disposisi-baru", {
      state: {
        selectedSurat: {
          surat_id: surat.surat_id,
          nomor_surat: surat.nomor_surat,
          perihal: surat.perihal,
          label: `${surat.nomor_surat} - ${surat.perihal}`,
          value: surat.surat_id,
        },
      },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white" mb={0}>
                  Surat Masuk
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                >
                  <MDInput
                    label="Cari (Nomor, Pengirim, atau Perihal)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      flexGrow: 1,
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    options={tahunList}
                    value={
                      selectedTahun
                        ? {
                            label: selectedTahun.toString(),
                            value: selectedTahun,
                          }
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedTahun(newValue.value);
                      } else {
                        setSelectedTahun(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Tahun"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 120,
                        }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={bulanList}
                    value={
                      selectedBulan
                        ? bulanList.find((b) => b.value === selectedBulan) ||
                          null
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedBulan(newValue.value);
                      } else {
                        setSelectedBulan(null);
                      }
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "object" ? option.label : String(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Bulan"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* Informasi dan Kontrol di Bagian Atas */}
                {!loading && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                    pt={2}
                  >
                    {/* Kiri: Dropdown Limit dan Sorting */}
                    <MDBox display="flex" gap={3} alignItems="flex-start">
                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Per Halaman
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={limitPerPage}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setLimitPerPage(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={[9, 10, 20, 50]}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>

                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Urutkan
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={sortBy}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setSortBy(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={["terbaru", "terlama"]}
                          getOptionLabel={(option) => {
                            const labels = {
                              terbaru: "Terbaru",
                              terlama: "Terlama",
                            };
                            return labels[option] || option;
                          }}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>
                    </MDBox>

                    {/* Kanan: Toggle View */}
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(e, newView) => {
                        if (newView !== null) setViewMode(newView);
                      }}
                      size="large"
                    >
                      <ToggleButton value="grid" aria-label="grid view">
                        <Icon sx={{ fontSize: 28 }}>grid_view</Icon>
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="list view">
                        <Icon sx={{ fontSize: 28 }}>view_list</Icon>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </MDBox>
                )}

                {/* Info Dataset dan Pagination */}
                {!loading && total > 0 && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                  >
                    <MDTypography variant="body2" color="text">
                      Menampilkan {(currentPage - 1) * limitPerPage + 1} sampai{" "}
                      {Math.min(currentPage * limitPerPage, total)} dari {total}{" "}
                      surat masuk
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Halaman {currentPage} dari {totalPages}
                    </MDTypography>
                  </MDBox>
                )}

                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox px={3}>
                    {filteredSuratList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data surat masuk
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchTerm
                            ? "Tidak ada data yang sesuai dengan pencarian"
                            : "Belum ada surat masuk yang terdaftar"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                        {viewMode === "grid" ? (
                          // Tampilan Grid (kotak-kotak)
                          <Grid container spacing={2}>
                            {filteredSuratList.map((surat) => {
                              const fotoUrl = surat.foto_pengupload || null;

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={surat.surat_id}
                                >
                                  <Card
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      borderRadius: 2,
                                      boxShadow: 2,
                                      backgroundColor: "#fff",
                                      transition: "box-shadow 0.3s",
                                      "&:hover": {
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    <MDBox
                                      p={2.5}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                      }}
                                    >
                                      {/* Tags di bagian atas */}
                                      <MDBox
                                        display="flex"
                                        flexDirection="column"
                                        gap={1}
                                        mb={1.5}
                                      >
                                        <MDBox
                                          display="flex"
                                          gap={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                        >
                                          {surat.sudah_didisposisi ? (
                                            <Chip
                                              label="Sudah Disposisi"
                                              size="small"
                                              sx={{
                                                backgroundColor: "#4caf50",
                                                color: "#fff",
                                                fontWeight: "medium",
                                                fontSize: "0.75rem",
                                              }}
                                            />
                                          ) : (
                                            <Chip
                                              label="Belum Disposisi"
                                              size="small"
                                              sx={{
                                                backgroundColor: "#ff9800",
                                                color: "#fff",
                                                fontWeight: "medium",
                                                fontSize: "0.75rem",
                                              }}
                                            />
                                          )}
                                        </MDBox>
                                        {/* Tanggal Disposisi */}
                                        {surat.sudah_didisposisi &&
                                          surat.tanggal_disposisi && (
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontSize: "0.7rem" }}
                                            >
                                              Disposisi:{" "}
                                              {formatDate(
                                                surat.tanggal_disposisi
                                              )}
                                            </MDTypography>
                                          )}
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox
                                        flex={1}
                                        display="flex"
                                        flexDirection="column"
                                      >
                                        {/* Nomor Surat */}
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                          color="text"
                                          mb={1}
                                          sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            minHeight: "3rem",
                                          }}
                                        >
                                          {surat.nomor_surat || "-"}
                                        </MDTypography>

                                        {/* Pengirim */}
                                        <MDBox mb={1}>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="medium"
                                          >
                                            Pengirim:
                                          </MDTypography>
                                          <MDTypography
                                            variant="body2"
                                            color="text"
                                            sx={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 1,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                            }}
                                          >
                                            {surat.pengirim || "-"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Perihal */}
                                        {surat.perihal && (
                                          <MDBox mb={1.5} flex={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                              fontWeight="medium"
                                            >
                                              Perihal:
                                            </MDTypography>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                minHeight: "4.8rem",
                                              }}
                                            >
                                              {surat.perihal}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1.5}
                                        >
                                          {fotoUrl ? (
                                            <Avatar
                                              src={fotoUrl}
                                              alt={
                                                surat.nama_pengupload || "User"
                                              }
                                              sx={{ width: 20, height: 20 }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 12 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {surat.nama_pengupload ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>
                                      </MDBox>

                                      {/* Footer: Timestamp dan Action buttons */}
                                      <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mt="auto"
                                        pt={1}
                                        sx={{
                                          borderTop: "1px solid #e0e0e0",
                                        }}
                                      >
                                        {/* Timestamp */}
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatRelativeTime(
                                            surat.tanggal_surat
                                          )}
                                        </MDTypography>

                                        {/* Action buttons */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                          flexWrap="wrap"
                                        >
                                          {/* Icon Download/Buka File */}
                                          {surat.file_surat && (
                                            <IconButton
                                              size="small"
                                              color="info"
                                              onClick={() =>
                                                handleFileClick(surat)
                                              }
                                              title={getFileButtonLabel(surat)}
                                            >
                                              {getFileIcon(surat)}
                                            </IconButton>
                                          )}
                                          {/* Tombol Disposisi - hanya untuk level_jabatan 1 atau 2 dan belum dispo */}
                                          {!surat.sudah_didisposisi &&
                                            (levelJabatan === 1 ||
                                              levelJabatan === 2) && (
                                              <MDButton
                                                variant="gradient"
                                                color="info"
                                                size="small"
                                                onClick={() =>
                                                  handleDisposisi(surat)
                                                }
                                                sx={{
                                                  minWidth: "auto",
                                                  px: 1.5,
                                                  py: 0.5,
                                                  fontSize: "0.7rem",
                                                }}
                                              >
                                                Disposisi
                                              </MDButton>
                                            )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : (
                          // Tampilan List (memanjang)
                          <>
                            {filteredSuratList.map((surat) => {
                              const fotoUrl = surat.foto_pengupload || null;

                              return (
                                <Card
                                  key={surat.surat_id}
                                  sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <MDBox p={2.5}>
                                    <MDBox display="flex" gap={2}>
                                      {/* Icon di kiri */}
                                      <MDBox
                                        sx={{
                                          minWidth: 60,
                                          height: 60,
                                          borderRadius: "50%",
                                          backgroundColor: "#2196f3",
                                          border: "2px solid #1976d2",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 30,
                                            color: "#fff",
                                          }}
                                        >
                                          mail
                                        </Icon>
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox flex={1}>
                                        {/* Header dengan nomor surat dan timestamp */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="start"
                                          mb={1}
                                        >
                                          <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="text"
                                            sx={{ flex: 1 }}
                                          >
                                            {surat.nomor_surat || "-"}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ ml: 2 }}
                                          >
                                            {formatRelativeTime(
                                              surat.tanggal_surat
                                            )}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Status Disposisi */}
                                        <MDBox
                                          mb={1}
                                          display="flex"
                                          flexDirection="column"
                                          gap={0.5}
                                        >
                                          <MDBox>
                                            {surat.sudah_didisposisi ? (
                                              <Chip
                                                label="Sudah Disposisi"
                                                size="small"
                                                sx={{
                                                  backgroundColor: "#4caf50",
                                                  color: "#fff",
                                                  fontWeight: "medium",
                                                }}
                                              />
                                            ) : (
                                              <Chip
                                                label="Belum Disposisi"
                                                size="small"
                                                sx={{
                                                  backgroundColor: "#ff9800",
                                                  color: "#fff",
                                                  fontWeight: "medium",
                                                }}
                                              />
                                            )}
                                          </MDBox>
                                          {/* Tanggal Disposisi */}
                                          {surat.sudah_didisposisi &&
                                            surat.tanggal_disposisi && (
                                              <MDTypography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: "0.7rem" }}
                                              >
                                                Disposisi:{" "}
                                                {formatDate(
                                                  surat.tanggal_disposisi
                                                )}
                                              </MDTypography>
                                            )}
                                        </MDBox>

                                        {/* Pengirim */}
                                        <MDBox mb={1}>
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Icon
                                              sx={{
                                                fontSize: 16,
                                                color: "text.secondary",
                                              }}
                                            >
                                              business
                                            </Icon>
                                            <MDTypography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {surat.pengirim || "-"}
                                            </MDTypography>
                                          </MDBox>
                                        </MDBox>

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1}
                                        >
                                          {fotoUrl ? (
                                            <Avatar
                                              src={fotoUrl}
                                              alt={
                                                surat.nama_pengupload || "User"
                                              }
                                              sx={{ width: 24, height: 24 }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 14 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {surat.nama_pengupload ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Perihal */}
                                        {surat.perihal && (
                                          <MDBox mb={1.5}>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {surat.perihal}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Action buttons - di kanan bawah */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="flex-end"
                                          alignItems="center"
                                          gap={0.5}
                                          mt={1.5}
                                          flexWrap="wrap"
                                        >
                                          {/* Tombol Download/Buka File */}
                                          {surat.file_surat && (
                                            <Button
                                              variant="outlined"
                                              color="info"
                                              size="small"
                                              onClick={() =>
                                                handleFileClick(surat)
                                              }
                                              startIcon={getFileIcon(surat)}
                                              sx={{
                                                textTransform: "none",
                                                minWidth: "auto",
                                              }}
                                            >
                                              {getFileButtonLabel(surat)}
                                            </Button>
                                          )}
                                          {/* Tombol Disposisi - hanya untuk level_jabatan 1 atau 2 dan belum dispo */}
                                          {!surat.sudah_didisposisi &&
                                            (levelJabatan === 1 ||
                                              levelJabatan === 2) && (
                                              <MDButton
                                                variant="gradient"
                                                color="info"
                                                size="small"
                                                onClick={() =>
                                                  handleDisposisi(surat)
                                                }
                                                sx={{
                                                  textTransform: "none",
                                                  minWidth: "auto",
                                                }}
                                              >
                                                Disposisi
                                              </MDButton>
                                            )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                </Card>
                              );
                            })}
                          </>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            mt={3}
                            mb={2}
                          >
                            <MDPagination variant="gradient" color="info">
                              {currentPage > 1 && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_left
                                  </Icon>
                                </MDPagination>
                              )}

                              {/* Tampilkan beberapa halaman */}
                              {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <MDPagination
                                      key={pageNum}
                                      item
                                      onClick={() => handlePageChange(pageNum)}
                                      active={currentPage === pageNum}
                                    >
                                      {pageNum}
                                    </MDPagination>
                                  );
                                }
                              )}

                              {totalPages > 5 &&
                                currentPage < totalPages - 2 && (
                                  <>
                                    <MDPagination item disabled>
                                      ...
                                    </MDPagination>
                                    <MDPagination
                                      item
                                      onClick={() =>
                                        handlePageChange(totalPages)
                                      }
                                    >
                                      {totalPages}
                                    </MDPagination>
                                  </>
                                )}

                              {currentPage < totalPages && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_right
                                  </Icon>
                                </MDPagination>
                              )}
                            </MDPagination>

                            {/* Input untuk lompat ke halaman */}
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={2}
                            >
                              <MDTypography variant="caption" color="text">
                                Lompat ke halaman:
                              </MDTypography>
                              <MDInput
                                type="number"
                                value={currentPage}
                                onChange={(e) => {
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= totalPages) {
                                    handlePageChange(page);
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  max: totalPages,
                                  style: {
                                    width: "60px",
                                    textAlign: "center",
                                  },
                                }}
                                sx={{ width: "80px" }}
                              />
                              <Button
                                variant="contained"
                                color="info"
                                size="small"
                                onClick={() => handlePageChange(currentPage)}
                                sx={{ minWidth: "auto", px: 2 }}
                              >
                                Go
                              </Button>
                            </MDBox>
                          </MDBox>
                        )}
                      </>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Surat Masuk</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFileUrl ? (
              <iframe
                src={selectedFileUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SuratMasukDokumen;
